/**
 * Утилиты для работы с DOM-элементами.
 * Расчёт позиций, измерения, взаимодействие с viewport.
 */

/**
 * Последняя позиция курсора в координатах viewport. Слушатель один на
 * приложение: координаты нужны потребителям в момент, когда события мыши
 * у них уже нет (открытие попапа, меню, тултипа).
 */
const lastCursorCoords: { x: number; y: number } = { x: 0, y: 0 };

window.addEventListener('mousemove', (event: MouseEvent) => {
  lastCursorCoords.x = event.clientX;
  lastCursorCoords.y = event.clientY;
}, { capture: true, passive: true });

/**
 * Левый верхний угол последнего открытого контекстного меню, в координатах
 * viewport. Пишет VContextMenu при монтировании, читает VPopup, когда ему
 * не передали якорь: попап, открытый пунктом меню, должен встать на месте
 * этого меню.
 */
let lastContextMenuCoords: { x: number; y: number } | null = null;

class DomUtils {
  /**
   * Позиция курсора в координатах viewport на момент вызова.
   * Возвращает копию — чтобы потребитель мог держать её как снимок.
   *
   * До первого движения мыши (открытие с клавиатуры, программный вызов)
   * отдаёт левый верхний угол окна.
   */
  static getCursorCoords(): { x: number; y: number } {
    return { x: lastCursorCoords.x, y: lastCursorCoords.y };
  }

  /** Запомнить положение открывшегося контекстного меню. Зовёт VContextMenu. */
  static setContextMenuCoords(element: HTMLElement): void {
    const box = element.getBoundingClientRect();

    lastContextMenuCoords = { x: box.left, y: box.top };
  }

  /**
   * Положение последнего открытого контекстного меню. null — меню не
   * открывалось или точку уже забрали.
   */
  static getContextMenuCoords(): { x: number; y: number } | null {
    const result = lastContextMenuCoords;

    lastContextMenuCoords = null;

    return result;
  }

  /**
   * Рассчитывает позицию тултипа относительно элемента-якоря.
   *
   * Стратегия:
   * 1. По умолчанию — снизу, по центру относительно якоря
   * 2. Если не влезает снизу — переворачиваем наверх
   * 3. Если вылезает за левый или правый край — прижимаем к краю
   *
   * @param anchor  — элемент, относительно которого позиционируем
   * @param tooltip  — элемент, который нужно расположить (должен быть в DOM)
   * @param gap     — отступ между якорем и тултипом в пикселях
   */
  static getTooltipPosition(
    anchor: HTMLElement,
    tooltip: HTMLElement,
    gap: number = 4,
  ): { top: number; left: number } {
    const anchorRect    = anchor.getBoundingClientRect();
    const tooltipWidth  = tooltip.offsetWidth;
    const tooltipHeight = tooltip.offsetHeight;

    // Позиция по умолчанию — сразу под якорем
    let top = anchorRect.bottom + gap;

    // Центрируем тултип горизонтально относительно якоря
    let left = anchorRect.left + (anchorRect.width / 2) - (tooltipWidth / 2);

    // Тултип не влезает снизу — переворачиваем наверх
    if (top + tooltipHeight > window.innerHeight) {
      top = anchorRect.top - tooltipHeight - gap;
    }

    // Тултип вылезает за левый край экрана — прижимаем к левому краю
    if (left < 8) {
      left = 8;
    }

    // Тултип вылезает за правый край экрана — прижимаем к правому краю
    if (left + tooltipWidth > window.innerWidth) {
      left = window.innerWidth - tooltipWidth - 8;
    }

    return { top, left };
  }
}

export default DomUtils;
